import {
    Button, Form, Modal
} from '@finbeat/react-toolkit';
import { AxiosError } from 'axios';
import { getWithdrawOrders, sendManualPayment } from 'axios/clientApi';
import { getDictionaryItems } from 'axios/dictionaryApi';
import useApi from 'hooks/useApi';
import useAuth from 'hooks/useAuth';
import { AxiosErrorResponse } from 'models/api/axiosErrorResponse';
import { ClientManualPaymentInfo, ClientTranscationResponse } from 'models/api/client';
import { useNotifyContext } from 'providers/NotifyProvider';
import { Dispatch, SetStateAction, useMemo, useState } from 'react';
import { Trans, useTranslation } from 'react-i18next';
import { useMutation, useQuery } from 'react-query';
import { ModalBody, ModalFooter, ModalHeader } from 'reactstrap';
import { useClientWorkplaceState } from '../Clients';
import ClientWithdrawMoneyForm from './ClientWithdrawMoneyForm';

type ClientWithdrawMoneyModalProps = {
    isOpen?: boolean;
    toggle: Dispatch<SetStateAction<boolean>>;
    clientId: string | number;
    cardId: string | number;
    cardNumber: string;
};

export type ClientWithdrawMoneyModalFormType = ClientManualPaymentInfo;

const ClientWithdrawMoneyModal = ({
    isOpen = false,
    toggle,
    clientId,
    cardId,
    cardNumber,
}: ClientWithdrawMoneyModalProps) => {
    const api = useApi();
    const { handleError } = useClientWorkplaceState();
    const { addSubscription, removeSubscription } = useNotifyContext();
    const { t } = useTranslation();
    const { hasRole } = useAuth();

    const [waitPaymentResult, setWaitPaymentResult] = useState<boolean>(false);
    const [transactionResult, setTransactionResult] = useState<ClientTranscationResponse | null>(
        null
    );

    const defaultValues = useMemo(
        () => ({
            cardId,
            clientId
        }),
        [clientId, cardId]
    );

    const { isFetching, data: orders } = useQuery(['clientWithdrawOrderList', clientId], () => getWithdrawOrders(api, clientId), {
        onError: (e: AxiosError<AxiosErrorResponse>) => handleError(e.response?.data),
        enabled: hasRole('clientsGetOrders') && clientId !== null
    });

    const { data: plumErrorCodes } = useQuery(
        ['plumErrorCodes'],
        () => getDictionaryItems(api, 'PlumErrorCodes'),
        {
            onError: (e: AxiosError<AxiosErrorResponse>) => handleError(e.response?.data),
            enabled: hasRole('dictionaryGetByCode')
        }
    );

    const saveChanges = (data: ClientWithdrawMoneyModalFormType) => {
        const manualPayment = { ...data };
        mutate(manualPayment);
    }

    const { isLoading, mutate } = useMutation(
        (data: ClientWithdrawMoneyModalFormType) => sendManualPayment(api, data),
        {
            onSuccess: (data) => {
                if (data.success && data.transactionId) {
                    setWaitPaymentResult(true);
                    addSubscription({
                        key: 'onTransactionManualResult',
                        type: 'TransactionManualResult',
                        callback: (message) => {
                            const trans = JSON.parse(message),
                                tran = trans.find(
                                    (t: ClientTranscationResponse) =>
                                        t.TransactionId === data.transactionId.toUpperCase()
                                );

                            if (tran) {
                                removeSubscription('TransactionManualResult');
                                setTransactionResult(tran);
                            }
                        },
                    });
                }
            },
            onError: (e) => handleError(e),
        }
    );

    return (
        <Modal isOpen={isOpen} toggle={() => toggle(false)}>
            <Form<ClientWithdrawMoneyModalFormType>
                onSubmit={(data) => saveChanges(data)}
                defaultValues={defaultValues}
                className="form-in-modal"
                clearAfterSubmit={false}
            >
                <ModalHeader toggle={() => toggle(false)}>
                    <Trans i18nKey="BackOfficeClientManualPayment" /> #{clientId}.
                    <p>
                        <Trans i18nKey="BackOfficeCardNumber" /> {cardNumber}.
                    </p>
                </ModalHeader>
                <ModalBody>
                    {!transactionResult && <ClientWithdrawMoneyForm
                        orders={orders ?? []}
                        ordersIsFetching={isFetching}
                        isLoading={isLoading}
                        waitPaymentResult={waitPaymentResult} />
                    }
                    {transactionResult && transactionResult.Status === 'Successfully' && (
                        <div className="alert alert-success" role="alert">
                            <h3 className="alert-heading">
                                <Trans i18nKey="BackOfficeManualPaymentSuccess" />
                            </h3>
                            <p>
                                <Trans i18nKey="BackOfficeOrderId" /> #{transactionResult.OrderId}
                            </p>
                            <p>
                                <Trans i18nKey="BackOfficeWithdrawAmount" />{' '}
                                {transactionResult.Amount}
                            </p>
                        </div>
                    )}
                    {transactionResult && transactionResult.Status === 'Error' && (
                        <div className="alert alert-danger" role="alert">
                            <h5 className="alert-heading">
                                <Trans i18nKey="BackOfficeManualPaymentFailed" />
                            </h5>
                            <p>
                                <Trans i18nKey="BackOfficeOrderId" /> #{transactionResult.OrderId}
                            </p>
                            <p>
                                {plumErrorCodes?.find((ec) => ec.code === transactionResult.Code)
                                    ? t(`BackOfficePlumErrorCodes${transactionResult.Code}`)
                                    : t('BackOfficePlumErrorCodesOTHER')}
                            </p>
                            {transactionResult.Code === "LOW_BALANCE" && (
                                <p>
                                    <Trans i18nKey="BackOfficeBalance" /> {(transactionResult.Balance ?? 0) / 100}
                                </p>
                            )}
                        </div>
                    )}
                </ModalBody>
                <ModalFooter>
                    {!transactionResult ? (
                        <>
                            <Button color="secondary" onClick={() => toggle(false)}>
                                <Trans i18nKey="BackOfficeClose" />
                            </Button>
                            <Button
                                type="submit"
                                color="success"
                                isPending={isLoading || waitPaymentResult}
                            >
                                <Trans i18nKey="BackOfficeSave" />
                            </Button>
                        </>
                    ) : (
                        <Button color="secondary" onClick={() => toggle(false)}>
                            <Trans i18nKey="BackOfficeOk" />
                        </Button>
                    )}
                </ModalFooter>
            </Form>
        </Modal>
    );
};

export default ClientWithdrawMoneyModal;
